import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useOnboarding,
  useResendOnboardingCode,
  useRetryOnboarding,
  useStartOnboarding,
  useSubmitOnboardingCode,
  useWhatsAppBusinessAccounts,
} from '@/hooks/useWhatsAppNumbers';
import { twilioNumbersApi } from '@/lib/api/twilio-numbers.api';
import { extractErrorMessage } from '@/lib/utils';

import { describeOnboarding } from './onboarding-view';

import type { WhatsAppOnboarding } from '@repo/shared-types';

const OTHER = '__other__';
const MARK: Record<string, string> = { todo: '○', active: '…', ok: '✓', failed: '✕' };

interface ProgressProps {
  record: WhatsAppOnboarding;
  busy: boolean;
  actionError?: unknown;
  onSubmitCode: (code: string) => void;
  onResend: () => void;
  onRetry: () => void;
}

export const OnboardingProgress = ({
  record,
  busy,
  actionError,
  onSubmitCode,
  onResend,
  onRetry,
}: ProgressProps) => {
  const [now, setNow] = useState(() => new Date());
  const [code, setCode] = useState('');

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const view = describeOnboarding(record, now);

  return (
    <div className="space-y-4">
      <ol className="space-y-1 text-sm">
        {view.steps.map((step) => (
          <li key={step.key} className="flex gap-2">
            <span aria-hidden className="w-4 text-center">
              {MARK[step.state]}
            </span>
            <span className={step.state === 'failed' ? 'text-destructive' : undefined}>
              {step.label}
            </span>
          </li>
        ))}
      </ol>

      {record.error && <p className="text-destructive text-sm">{record.error}</p>}

      {actionError ? (
        <p className="text-destructive text-sm">{extractErrorMessage(actionError)}</p>
      ) : null}

      {record.status === 'waiting_code' && !view.showCodeBox && (
        <p className="text-muted-foreground text-sm">
          Waiting for the code by SMS… You can type it by hand in {view.secondsUntilCodeBox ?? 0}s.
        </p>
      )}

      {record.status === 'waiting_code' && view.showCodeBox && (
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitCode(code);
          }}
        >
          <div className="flex-1 space-y-1">
            <label htmlFor="wa-code" className="text-xs font-medium">
              Verification code
            </label>
            <Input
              id="wa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy || code.replace(/\D/g, '').length !== 6}>
            Verify
          </Button>
        </form>
      )}

      {(record.status === 'waiting_code' ||
        (record.status === 'failed' && record.step === 'request_code')) && (
        <Button variant="outline" size="sm" onClick={onResend} disabled={busy}>
          Send again
        </Button>
      )}

      {record.status === 'failed' && (
        <Button onClick={onRetry} disabled={busy}>
          Retry
        </Button>
      )}

      {record.status === 'done' && (
        <div className="space-y-1 text-sm">
          <p>Number added. It is now in the list below.</p>
          {record.calling_error && (
            <p className="text-amber-600">
              Calling was not enabled: {record.calling_error}. You can retry from the list.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddNumberDialog = ({ open, onOpenChange }: DialogProps) => {
  const { data: accounts = [] } = useWhatsAppBusinessAccounts();
  const { data: twilioNumbers = [] } = useQuery({
    queryKey: ['twilio-numbers'],
    queryFn: () => twilioNumbersApi.list(),
    enabled: open,
  });
  const smsNumbers = useMemo(
    () => twilioNumbers.filter((n) => n.is_active && n.capabilities?.sms),
    [twilioNumbers],
  );

  const [wabaId, setWabaId] = useState('');
  const [twilioId, setTwilioId] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [method, setMethod] = useState<'SMS' | 'VOICE'>('SMS');
  const [onboardingId, setOnboardingId] = useState<string | null>(null);

  const start = useStartOnboarding();
  const { data: record } = useOnboarding(onboardingId);
  const submit = useSubmitOnboardingCode();
  const resend = useResendOnboardingCode();
  const retry = useRetryOnboarding();

  const isOther = twilioId === OTHER;
  const pickedTwilio = smsNumbers.find((n) => n.id === twilioId);
  const phoneValue = isOther ? phone : (pickedTwilio?.phone_number ?? '');

  const resetAll = () => {
    setOnboardingId(null);
    setWabaId('');
    setTwilioId('');
    setPhone('');
    setName('');
    setMethod('SMS');
    start.reset();
    submit.reset();
    resend.reset();
    retry.reset();
  };

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (next) return;
    // Keep an in-progress onboarding alive across a close so reopening resumes
    // it instead of showing a blank form and risking a duplicate start.
    const inProgress = record?.status === 'running' || record?.status === 'waiting_code';
    if (inProgress) return;
    resetAll();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add WhatsApp number</DialogTitle>
          <DialogDescription>
            Adds the number to the business account, verifies it, registers it and enables calling.
          </DialogDescription>
        </DialogHeader>

        {!record ? (
          <form
            className="space-y-3"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const created = await start.mutateAsync({
                  waba_id: wabaId,
                  phone_number: phoneValue,
                  display_name: name,
                  twilio_phone_number_id: isOther ? null : twilioId,
                  code_method: isOther ? method : 'SMS',
                });
                setOnboardingId(created.id);
              } catch {
                // start.error is shown under the form.
              }
            }}
          >
            <div className="space-y-1">
              <label className="text-xs font-medium">Business account</label>
              <Select value={wabaId} onValueChange={setWabaId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a business account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts
                    .filter((a) => a.is_active)
                    .map((a) => (
                      <SelectItem key={a.id} value={a.waba_id}>
                        {a.name || a.waba_id}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium">Number</label>
              <Select value={twilioId} onValueChange={setTwilioId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a Twilio number" />
                </SelectTrigger>
                <SelectContent>
                  {smsNumbers.map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {n.phone_number} · {n.messaging_region?.toUpperCase()}
                    </SelectItem>
                  ))}
                  <SelectItem value={OTHER}>Other number (enter the code by hand)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isOther && (
              <>
                <div className="space-y-1">
                  <label htmlFor="wa-phone" className="text-xs font-medium">
                    Phone number
                  </label>
                  <Input
                    id="wa-phone"
                    placeholder="+447700900123"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium">Send the code by</label>
                  <Select value={method} onValueChange={(v) => setMethod(v as 'SMS' | 'VOICE')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SMS">SMS</SelectItem>
                      <SelectItem value="VOICE">Voice call</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="space-y-1">
              <label htmlFor="wa-name" className="text-xs font-medium">
                Display name
              </label>
              <Input
                id="wa-name"
                placeholder="We Finance Any Car"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {start.error && (
              <p className="text-destructive text-sm">{extractErrorMessage(start.error)}</p>
            )}

            <DialogFooter>
              <Button
                type="submit"
                disabled={start.isPending || !wabaId || !phoneValue || name.trim().length < 3}
              >
                Add number
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <OnboardingProgress
            record={record}
            busy={submit.isPending || resend.isPending || retry.isPending}
            actionError={submit.error ?? resend.error ?? retry.error}
            onSubmitCode={(code) => {
              // Only the latest action's error should ever be shown — clear the
              // other two before firing this one so a stale error can't linger
              // past a different, successful action.
              resend.reset();
              retry.reset();
              submit.mutate({ id: record.id, extra: code });
            }}
            onResend={() => {
              submit.reset();
              retry.reset();
              resend.mutate({ id: record.id });
            }}
            onRetry={() => {
              submit.reset();
              resend.reset();
              retry.mutate({ id: record.id });
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
